import React, { useState, useEffect } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { EbayRequestsProvider, useEbayRequests } from '../hooks/useEbayRequests.jsx'
import { ReverbOffersProvider, useReverbOffers } from '../hooks/useReverbOffers.jsx'
import { api } from '../utils/api'
import PinnedPanel from './shared/PinnedPanel'
import FeedbackButton from './shared/FeedbackButton'
import { ToastContainer } from '../hooks/useToast.jsx'
import EnvironmentTitle from './EnvironmentTitle'

// Icons (simple SVG components)
const Icons = {
  menu: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 12h18M3 6h18M3 18h18" />
    </svg>
  ),
  records: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4z" />
      <path d="M5 4H3v2a3 3 0 0 0 3 3M19 4h2v2a3 3 0 0 1-3 3" />
    </svg>
  ),
  marketing: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
  deals: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    </svg>
  ),
  inventory: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  orders: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 12h6M9 16h6" />
    </svg>
  ),
  warehouse: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
    </svg>
  ),
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  customers: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />
    </svg>
  ),
  tickets: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  admin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  switchUser: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <path d="M20 8v6M23 11l-3 3-3-3" />
    </svg>
  ),
  pricing: (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <text x="12" y="19.5" textAnchor="middle" fontSize="23" fontWeight="600" fontFamily="inherit">£</text>
    </svg>
  ),
  pim: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 7V4h16v3M9 20h6M12 4v16" />
    </svg>
  ),
  testing: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 3h6M12 3v7l-5 8h10l-5-8V3" />
    </svg>
  )
}

const navigationConfig = [
  {
    id: 'tickets',
    name: 'Tickets',
    path: '/tickets',
    icon: 'tickets'
  },
  {
    id: 'deals',
    name: 'Deals',
    path: '/deals',
    icon: 'deals',
    children: [
      { id: 'deal-search', name: 'Search', path: '/deals/search' },
      { id: 'all-deals', name: 'All Deals', path: '/deals/all' },
      { id: 'create-deal', name: 'Create Deal', path: '/deals/create' },
      { id: 'quote-requested', name: 'Quote Requested', path: '/deals/quote-requested' },
      { id: 'quote-assessment', name: 'Quote Assessment', path: '/deals/quote-assessment' },
      { id: 'collections', name: 'Collections', path: '/deals/collections' },
      { id: 'warehouse-dropoff', name: 'Warehouse Drop-off', path: '/deals/warehousedropoff' },
      { id: 'payments', name: 'Payments', path: '/deals/payments' },
      { id: 'store-credit', name: 'Store credit', path: '/deals/store-credit' },
      { id: 'consignments', name: 'Consignments', path: '/deals/consignments' },
      { id: 'exceptions', name: 'Exceptions', path: '/deals/exceptions' },
      { id: 'remainders', name: 'Remainders', path: '/deals/remainders' },
      { id: 'audio-raider', name: 'Audio Raider', path: '/deals/audio-raider' }
    ]
  },
  {
    // Visible to every signed-in user, by decision (Benjamin, 2026-08-18).
    // It was gated on `deals`, which hid it from Customer Service — the people
    // who most need a customer record, and whose own support conversations the
    // page surfaces.
    //
    // This includes the payment card, so any signed-in operator can reveal a
    // customer's bank details. That is deliberate and not free: every reveal
    // still writes an audit line naming who looked, and nothing unmasks without
    // a click.
    id: 'customers',
    name: 'Customers',
    path: '/customers',
    icon: 'customers',
    alwaysVisible: true
  },
  {
    id: 'inventory',
    name: 'Inventory',
    path: '/inventory',
    icon: 'inventory',
    children: [
      { id: 'inventory-list', name: 'Inventory', path: '/inventory/list' },
      { id: 'stock-check', name: 'Stock Check', path: '/inventory/stock-check' },
      { id: 'drafts', name: 'Drafts Manager', path: '/inventory/drafts' },
      { id: 'activation-history', name: 'Activation History', path: '/inventory/activation-history' },
      { id: 'collections', name: 'Collections', path: '/inventory/collections' },
      { id: 'printing', name: 'Printing', path: '/inventory/printing' }
    ]
  },
  {
    id: 'orders',
    name: 'Orders',
    path: '/orders',
    icon: 'orders',
    children: [
      { id: 'orders-list', name: 'Orders', path: '/orders/list' },
      { id: 'cases', name: 'Cases', path: '/orders/cases' },
      { id: 'reverb-offers', name: 'Reverb Offers', path: '/orders/reverb-offers' },
      { id: 'return-items', name: 'Return Items', path: '/orders/returns' },
      { id: 'printruns', name: 'Printruns', path: '/orders/printruns' }
    ]
  },
  {
    id: 'warehouse',
    name: 'Warehouse',
    path: '/warehouse',
    icon: 'warehouse',
    children: [
      { id: 'receiving', name: 'Receiving', path: '/warehouse/receiving' },
      { id: 'testing', name: 'Testing', path: '/warehouse/testing' },
      { id: 'partially-tested', name: 'Partially Tested', path: '/warehouse/partially-tested' },
      { id: 'issue-trades', name: 'Issue Trades', path: '/warehouse/issue-trades' },
      { id: 'to-send-back', name: 'To Send Back', path: '/warehouse/to-send-back' },
      { id: 'repairs', name: 'Repairs', path: '/warehouse/repairs' },
      { id: 'photography', name: 'Photography', path: '/warehouse/photography' },
      { id: 'putaway', name: 'Putaway', path: '/warehouse/putaway' },
      { id: 'order-picking', name: 'Order Picking', path: '/warehouse/order-picking' },
      { id: 'shipping', name: 'Order Shipping', path: '/warehouse/shipping' },
      { id: 'create-product', name: 'Create Product', path: '/warehouse/manual-listing' }
    ]
  },
  {
    // id stays 'pim': it is the module permission gate. The legacy PIM
    // screens left the menu when the Catalog section replaced them
    // (KG Phase 1b); /pim/* routes stay URL-reachable until teardown.
    id: 'pim',
    name: 'Catalog',
    path: '/catalog',
    icon: 'pim',
    children: [
      { id: 'catalog-review', name: 'Review Queue', path: '/catalog/review', adminOnly: true },
      { id: 'catalog-proposals', name: 'Proposals', path: '/catalog/proposals' },
      { id: 'catalog-settings', name: 'Engine Settings', path: '/catalog/settings' }
    ]
  },
  {
    id: 'pricing',
    name: 'Pricing',
    path: '/pricing',
    icon: 'pricing',
    children: [
      { id: 'pricing-management', name: 'Pricing Management', path: '/pricing/management' },
      { id: 'pricing-calendar', name: 'Pricing Calendar', path: '/pricing/calendar' },
      { id: 'pricing-rules', name: 'Pricing Rules', path: '/pricing/rules' },
      { id: 'pricing-audit-log', name: 'Audit Log', path: '/pricing/audit-log' }
    ]
  },
  {
    id: 'marketing',
    name: 'Marketing',
    path: '/marketing',
    icon: 'marketing',
    children: [
      { id: 'staff-picks', name: 'Staff Picks', path: '/marketing/staff-picks' },
      { id: 'notify-me', name: 'Notify Me Products', path: '/marketing/notify-me' },
      { id: 'abandoned-trade-forms', name: 'Abandoned trade forms', path: '/marketing/abandoned-trade-forms' },
      { id: 'account-deletions', name: 'Account deletions', path: '/marketing/account-deletions', adminOnly: true },
      { id: 'trade-boost', name: 'Trade Boost', path: '/marketing/trade-boost', adminOnly: true },
      { id: 'referrals', name: 'Referrals', path: '/marketing/referrals', adminOnly: true }
    ]
  },
  {
    id: 'records',
    name: 'Records',
    path: '/records',
    icon: 'records',
    alwaysVisible: true
  }
]

const EBAY_ALERT_TITLE = 'An eBay request is close to its deadline'
// The menu entries the eBay deadline warning sits on (spec 20261013).
const EBAY_ALERT_SECTION = 'orders'
const EBAY_ALERT_CHILD = 'cases'
// The yellow "!" for Reverb Buyer Offers waiting for our reply (spec 20261015).
export const REVERB_OFFERS_WARNING_TITLE = 'A Reverb buyer offer is waiting for a reply'
const REVERB_OFFERS_CHILD = 'reverb-offers'

// alert = the red "!" (needs action now); warning = the yellow "!" (worth a
// look). The section shows both when both are on; each child shows its own.
export function NavItem({
  item, location, onNavigate, alert, alertTitle, alertChildId,
  warning, warningTitle, warningChildId, isAdmin
}) {
  const isActive = location.pathname.startsWith(item.path)
  const childItems = item.children?.filter(child => !child.adminOnly || isAdmin)
  const showChildren = isActive && childItems?.length
  const badgeTitle = alertTitle || 'You have items that need attention'

  return (
    <div className="nav-section">
      <NavLink
        to={item.path}
        className={`nav-item ${isActive ? 'active' : ''}`}
        onClick={onNavigate}
      >
        <span className="nav-item-icon">
          {Icons[item.icon]}
        </span>
        {item.name}
        {alert && (
          <span className="nav-alert-badge" title={badgeTitle} aria-label={badgeTitle}>!</span>
        )}
        {warning && <WarningBadge title={warningTitle} />}
      </NavLink>

      {showChildren && (
        <div className="nav-children">
          {childItems.map(child => (
            <NavLink
              key={child.id}
              to={child.path}
              className={({ isActive }) => `nav-child ${isActive ? 'active' : ''}`}
              onClick={onNavigate}
            >
              {child.name}
              {alert && child.id === alertChildId && (
                <span className="nav-alert-badge" title={badgeTitle} aria-label={badgeTitle}>!</span>
              )}
              {warning && child.id === warningChildId && <WarningBadge title={warningTitle} />}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}

export function WarningBadge({ title }) {
  return (
    <span className="nav-alert-badge nav-alert-badge--warning" title={title} aria-label={title}>!</span>
  )
}

function ConnectionBanner() {
  const { connectionError, checkAuth } = useAuth()

  if (!connectionError) return null

  return (
    <div className="connection-banner">
      <span>Connection issue — retrying automatically...</span>
      <button onClick={checkAuth} className="btn btn-sm btn-ghost">
        Retry now
      </button>
    </div>
  )
}

// Which top-level nav items a user sees. A restricted Quote Reviewer is a
// normal Deals user in the nav: only the Quote Assessment PAGE changes for
// them (restricted actions + assigned-only scope), never the menu (spec
// docs/archive/202609/specs_20260916_quote_review.md). Pure + exported for tests.
// Every user gets the Admin link. Admins land on Users; everyone else can only
// open Clicky Text, so they land there.
export function adminLinkFor(user) {
  return user?.is_admin ? '/admin/users' : '/admin/clicky-text'
}

export function buildVisibleNav(config, { hasModule }) {
  return config.filter(item => item.alwaysVisible || hasModule(item.id))
}

function Layout() {
  const { user, logout, hasModule, hasPermission, connectionError } = useAuth()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [pinnedPanelOpen, setPinnedPanelOpen] = useState(false)
  const [pinnedCount, setPinnedCount] = useState(0)
  const [assignedTicketCount, setAssignedTicketCount] = useState(0)
  const [assignedQuoteCount, setAssignedQuoteCount] = useState(0)
  const { view: ebayRequests } = useEbayRequests()
  const { awaitingCount: reverbOffersAwaiting } = useReverbOffers()

  // Load pinned count on mount and periodically
  useEffect(() => {
    loadPinnedCount()
    const interval = setInterval(loadPinnedCount, 60000) // refresh every minute
    return () => clearInterval(interval)
  }, [])

  async function loadPinnedCount() {
    try {
      const response = await api.get('/api/pinned/count?filter=mine')
      setPinnedCount(response.count || 0)
    } catch (e) {
      // Ignore errors silently
    }
  }

  // Load count of unresolved tickets assigned to me — powers the Tickets nav alert.
  // Refetch on every navigation (so the badge updates without a full page reload)
  // and poll every minute as a fallback for changes made elsewhere.
  const canSeeTickets = hasModule('tickets')
  useEffect(() => {
    if (!canSeeTickets) return
    loadAssignedTicketCount()
    const interval = setInterval(loadAssignedTicketCount, 60000) // refresh every minute
    // Refresh immediately when tickets are assigned/resolved on the Tickets page
    window.addEventListener('tickets:changed', loadAssignedTicketCount)
    return () => {
      clearInterval(interval)
      window.removeEventListener('tickets:changed', loadAssignedTicketCount)
    }
  }, [canSeeTickets, location.pathname])

  async function loadAssignedTicketCount() {
    try {
      const response = await api.get('/api/tickets/assigned-count')
      setAssignedTicketCount(response.count || 0)
    } catch (e) {
      // Ignore errors silently
    }
  }

  // Count of quotes assigned to me for second review — powers the Deals nav
  // alert (spec docs/archive/202609/specs_20260916_quote_review.md). Same pattern as tickets:
  // fetch on mount, poll, refetch on navigation, and react to a page event.
  const canReviewQuotes = hasModule('deals')
  useEffect(() => {
    if (!canReviewQuotes) return
    loadAssignedQuoteCount()
    const interval = setInterval(loadAssignedQuoteCount, 60000)
    window.addEventListener('quote-review:changed', loadAssignedQuoteCount)
    return () => {
      clearInterval(interval)
      window.removeEventListener('quote-review:changed', loadAssignedQuoteCount)
    }
  }, [canReviewQuotes, location.pathname])

  async function loadAssignedQuoteCount() {
    try {
      const response = await api.get('/api/deals/reviews/assigned-count')
      setAssignedQuoteCount(response.count || 0)
    } catch (e) {
      // Ignore errors silently
    }
  }

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false)
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [mobileMenuOpen])

  // Filter navigation based on user permissions
  const visibleNav = buildVisibleNav(navigationConfig, { hasModule })

  // Which menu sections carry the red "!" and why. An eBay request that is
  // amber, red or overdue lights Orders and its Cases entry.
  const navAlerts = {
    tickets: { on: assignedTicketCount > 0, title: 'You have tickets assigned to you' },
    deals: { on: assignedQuoteCount > 0, title: 'You have quotes assigned for review' },
    [EBAY_ALERT_SECTION]: { on: ebayRequests.attention_count > 0, title: EBAY_ALERT_TITLE, childId: EBAY_ALERT_CHILD }
  }
  // Yellow marks: worth a look, not urgent. Reverb Buyer Offers waiting for us.
  const navWarnings = {
    [EBAY_ALERT_SECTION]: { on: reverbOffersAwaiting > 0, title: REVERB_OFFERS_WARNING_TITLE, childId: REVERB_OFFERS_CHILD }
  }

  return (
    <div className="app-layout">
      {/* Mobile Header */}
      <header className="mobile-header">
        <button
          className="mobile-menu-btn"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open menu"
        >
          <span className="mobile-menu-icon">{Icons.menu}</span>
        </button>
        <div className="mobile-logo">
          <img src="/prism_logo_yel.png" alt="Prism" className="logo-image-mobile" />
          <EnvironmentTitle className="env-title" />
        </div>
        <div className="mobile-header-spacer" />
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Sidebar - becomes slide-out drawer on mobile */}
      <aside className={`app-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/prism_logo_yel.png" alt="Prism" className="logo-image" />
            <EnvironmentTitle className="env-title" />
          </div>
          <button
            className="mobile-close-btn"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            {Icons.close}
          </button>
        </div>

        <nav className="sidebar-nav">
          {user?.is_admin && (
            <div className="nav-section">
              <NavLink
                to="/"
                className={({ isActive }) => `nav-item ${isActive && location.pathname === '/' ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="nav-item-icon">{Icons.dashboard}</span>
                Dashboard
              </NavLink>
            </div>
          )}

          {/* Pinned Items Badge */}
          <div className="nav-section">
            <button
              className="nav-item pinned-badge-btn"
              onClick={() => { setPinnedPanelOpen(true); setMobileMenuOpen(false) }}
            >
              <span className="nav-item-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </span>
              Pinned
              {pinnedCount > 0 && (
                <span className="pinned-count-badge">{pinnedCount}</span>
              )}
            </button>
          </div>

          {visibleNav.map(item => (
            <NavItem
              key={item.id}
              item={item}
              location={location}
              onNavigate={() => setMobileMenuOpen(false)}
              alert={navAlerts[item.id]?.on}
              alertTitle={navAlerts[item.id]?.title}
              alertChildId={navAlerts[item.id]?.childId}
              warning={navWarnings[item.id]?.on}
              warningTitle={navWarnings[item.id]?.title}
              warningChildId={navWarnings[item.id]?.childId}
              isAdmin={user?.is_admin}
            />
          ))}

          {/* Admin Section - everyone gets it; non-admins only reach Clicky Text */}
          {user && (
            <div className="nav-section">
              <NavLink
                to={adminLinkFor(user)}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="nav-item-icon">{Icons.admin}</span>
                Admin
              </NavLink>
            </div>
          )}

          {/* Testing Section (admin only) */}
          {user?.is_admin && (
            <div className="nav-section">
              <NavLink
                to="/testing/payment-email"
                className={({ isActive }) => `nav-item ${location.pathname.startsWith('/testing') ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="nav-item-icon">{Icons.testing}</span>
                Testing
              </NavLink>
              {location.pathname.startsWith('/testing') && (
                <div className="nav-children">
                  <NavLink
                    to="/testing/payment-email"
                    className={({ isActive }) => `nav-child ${isActive ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Payment Confirmation Email
                  </NavLink>
                  <NavLink
                    to="/testing/quote-dummy"
                    className={({ isActive }) => `nav-child ${isActive ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Quote Dummy
                  </NavLink>
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="sidebar-user">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
            <span className="nav-item-icon">{Icons.user}</span>
            <div>
              <div style={{ fontWeight: 500 }}>{user?.name}</div>
              <div className="text-sm text-muted">
                {user?.is_admin ? 'Admin' : 'Employee'}
              </div>
            </div>
          </div>
          <button
            onClick={logout}
            className="btn btn-ghost"
            style={{ width: '100%', justifyContent: 'flex-start' }}
          >
            <span className="nav-item-icon">{Icons.logout}</span>
            Logout
          </button>
        </div>
      </aside>

      <main className="app-main">
        <ConnectionBanner />
        <div className="app-content">
          <Outlet />
        </div>
      </main>

      {/* Pinned Items Panel */}
      <PinnedPanel
        isOpen={pinnedPanelOpen}
        onClose={() => setPinnedPanelOpen(false)}
        onCountChange={setPinnedCount}
      />

      {/* Feedback Widget */}
      <FeedbackButton />

      {/* The one toast renderer for every page and modal (useToast has no
          provider; a second mount would show each toast twice). */}
      <ToastContainer />
    </div>
  )
}

// The eBay requests view and the Reverb offer count are loaded here, once,
// for the menu marks and for every Orders screen below it. Only users who can
// see Orders load them.
function LayoutWithEbayRequests() {
  const { hasModule } = useAuth()
  const canSeeOrders = hasModule(EBAY_ALERT_SECTION)
  return (
    <EbayRequestsProvider enabled={canSeeOrders}>
      <ReverbOffersProvider enabled={canSeeOrders}>
        <Layout />
      </ReverbOffersProvider>
    </EbayRequestsProvider>
  )
}

export default LayoutWithEbayRequests
